import { type PendingKeys, type SyncOp, attendanceKey } from '@cadoan/shared'

/**
 * Writes made on the phone wait here until the API has them.
 *
 * One entry per record, keyed so a newer write to the same record replaces the
 * older one: tapping someone present, then absent, then present again offline
 * sends one op, not three. The replaced ops carried no information the last one
 * does not — last write wins anyway.
 */
export interface OutboxEntry {
  key: string
  op: SyncOp
}

export function opKey(op: SyncOp): string {
  return op.kind === 'meeting'
    ? `meeting|${op.id}`
    : `attendance|${attendanceKey(op.meetingId, op.memberId)}`
}

export const toEntry = (op: SyncOp): OutboxEntry => ({ key: opKey(op), op })

/** Ops per request. The API accepts 500; smaller batches keep a retry cheap. */
export const PUSH_CHUNK = 100

/**
 * Meetings first: a meeting created offline must exist on the server before
 * the marks taken at it arrive, or they are rejected as `meeting_not_found`.
 */
export function orderForPush(entries: readonly OutboxEntry[]): OutboxEntry[] {
  const rank = (e: OutboxEntry) => (e.op.kind === 'meeting' ? 0 : 1)
  return [...entries].sort((a, b) => rank(a) - rank(b) || a.op.at - b.op.at)
}

/** Records with a local write still queued: a pull must not overwrite them. */
export function pendingKeys(entries: readonly OutboxEntry[]): PendingKeys {
  const out = { meetings: new Set<string>(), attendance: new Set<string>() }
  for (const { op } of entries) {
    if (op.kind === 'meeting') out.meetings.add(op.id)
    else out.attendance.add(attendanceKey(op.meetingId, op.memberId))
  }
  return out
}
